import { html, LitElement, nothing, TemplateResult } from 'lit'

import {
  SelectingPaymentMethodEvent,
  StartPaymentEvent,
  StartPaymentFailedEvent,
  StartPaymentPayloadCreationFailedEvent,
} from 'components/ordering/checkout/events.js'
import {
  PAYMENT_BACKEND,
  PaymentBackend
} from 'components/ordering/checkout/payment-methods/payment-backend/payment-backend.js'
import {
  SelectPaymentMethodRequest
} from 'components/ordering/checkout/payment-methods/payment-backend/select-payment-method-api.js'
import {
  CouldNotCreatePaymentPayload,
  StartMollieKlarnaPaymentPayload, StartMolliePaymentPayload,
  StartMolliePaymentRequest,
} from 'components/ordering/checkout/payment-methods/payment-backend/start-payment-api.js'
import {
  StartPaymentError,
  StartPaymentSuccess
} from 'components/ordering/checkout/payment-methods/payment-backend/start-payment-parser.js'
import {
  PAYMENT_URL_STORE,
  PaymentUrlStore
} from 'components/ordering/checkout/payment-methods/payment-backend/payment-url-store.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'


export type PaymentMethodState = 'selectable' | 'selected' | 'loading'


export abstract class CSCPaymentMethod extends LitElement {
  @inject(PAYMENT_BACKEND)
  protected paymentBackend!: PaymentBackend

  @inject(WINDOW_LOCATION)
  protected windowLocation!: WindowLocation

  @inject(PAYMENT_URL_STORE)
  protected paymentUrlStore!: PaymentUrlStore

  protected abstract paymentMethodId: string
  protected abstract customDescription: string | null
  protected abstract state: PaymentMethodState

  protected abstract renderPaymentMethodContent: () => TemplateResult
  protected abstract selectPaymentMethod: () => Promise<void>
  protected abstract startPayment: () => Promise<void>

  override connectedCallback() {
    super.connectedCallback()

    window.addEventListener(StartPaymentEvent.type, this.startPayment)
    window.addEventListener(SelectingPaymentMethodEvent.type, this.updateSelectedState)
  }

  override disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener(StartPaymentEvent.type, this.startPayment)
    window.removeEventListener(SelectingPaymentMethodEvent.type, this.updateSelectedState)
  }

  protected updateSelectedState = (event: SelectingPaymentMethodEvent) => {
    if (this.paymentMethodId === event.methodId) {
      this.state = 'loading'
    } else {
      this.state = 'selectable'
    }
  }

  protected override render(): TemplateResult {
    return html`
      <cse-selectable
        state="${this.state}"
        @click="${this.selectPaymentMethod}"
      >
        ${this.renderPaymentMethodContent()}
        ${this.customDescription ? html`
          <cse-text variant="body-s">${this.customDescription}</cse-text>
        ` : nothing }
      </cse-selectable>
    `
  }
}

export abstract class CSCMolliePaymentMethod extends CSCPaymentMethod {
  protected abstract getPayload: () => Promise<StartMollieKlarnaPaymentPayload | StartMolliePaymentPayload  | CouldNotCreatePaymentPayload>

  protected selectPaymentMethod = async (): Promise<void> => {
    if (this.state !== 'selectable') { return }

    this.dispatchEvent(new SelectingPaymentMethodEvent(this.paymentMethodId))

    const selectPaymentMethodRequest = new SelectPaymentMethodRequest(
      this.paymentUrlStore.mollie.selectPaymentMethodUrl,
      this.paymentMethodId,
    )

    await this.paymentBackend.selectPaymentMethod(selectPaymentMethodRequest)
    this.windowLocation.reload()
  }

  protected startPayment = async (): Promise<void> => {
    if (this.state !== 'selected') { return }

    const startPaymentPayload = await this.getPayload()

    if (startPaymentPayload instanceof CouldNotCreatePaymentPayload) {
      this.dispatchEvent(new StartPaymentPayloadCreationFailedEvent())
      return
    }

    const startPaymentRequest = new StartMolliePaymentRequest(
      this.paymentUrlStore.mollie.startPaymentUrl,
      {
        startPaymentPayload: startPaymentPayload,
        pspIdentifier: 'mollie',
      },
    )

    const response = await this.paymentBackend.startMolliePayment(startPaymentRequest)

    if (response instanceof StartPaymentSuccess) {
      this.windowLocation.redirect(response.checkoutUrl)
    }

    if (response instanceof StartPaymentError) {
      this.dispatchEvent(new StartPaymentFailedEvent(response.error))
    }
  }
}
